/**
 * A merchant relayed a shopper's `customers/data_request` (GDPR SAR). We email the store
 * owner everything we hold on that shopper, within 30 days. Email only — it carries the
 * assembled data, so there is no meaningful Telegram rendering.
 */

import type { TenantEventDefinition } from './definition'

export const SHOPIFY_DATA_REQUEST_EVENT_TYPE = 'shopify_data_request' as const

export interface ShopifyDataRequestPayload {
  shopDomain: string
  customerId: string | null
  customerEmail: string | null
  ordersIncluded: number
  /** Pretty-printed JSON of everything we hold on the shopper, inlined into the email. */
  dataJson: string
}

function isShopifyDataRequestPayload(payload: unknown): payload is ShopifyDataRequestPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return (
    typeof p.shopDomain === 'string' &&
    (p.customerId === null || typeof p.customerId === 'string') &&
    (p.customerEmail === null || typeof p.customerEmail === 'string') &&
    typeof p.ordersIncluded === 'number' &&
    typeof p.dataJson === 'string'
  )
}

export const shopifyDataRequestEventDefinition: TenantEventDefinition<ShopifyDataRequestPayload> =
  {
    eventType: SHOPIFY_DATA_REQUEST_EVENT_TYPE,
    supportedChannelTypes: ['email'],
    validatePayload: isShopifyDataRequestPayload,
    tenant: {
      label: 'Shopify customer data request',
      description: "Shopify relayed a shopper's request for their data; the store owner receives everything held on that shopper.",
      recipients: 'store_owner',
      // A legal obligation (GDPR subject access), so the platform cannot switch it off.
      mandatory: true,
    },
  }
